import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { members as mockMembers } from "@/mocks/members";
import { normalizeWhatsapp } from "@/lib/whatsapp";
import CommunityNavbar from "./components/CommunityNavbar";
import MemberGrid from "./components/MemberGrid";

interface Member {
  id: string;
  name: string;
  profession: string;
  description: string;
  photo: string;
  photo_position?: string;
  whatsapp: string | null;
  email: string;
  instagram?: string;
  city_state?: string;
  website?: string;
  services?: string;
}

export default function Community() {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [professionFilter, setProfessionFilter] = useState("");
  const [searchParams] = useSearchParams();

  // Ler query string da URL e inicializar busca
  useEffect(() => {
    const q = searchParams.get("q");
    if (q) {
      setSearch(q);
    }
  }, [searchParams]);

  useEffect(() => {
    async function load() {
      const { data: { session } } = await supabase.auth.getSession();
      const fonte = session ? "members_directory" : "members_public";
      let { data, error } = await supabase
        .from(fonte)
        .select("*")
        .eq("status", "active")
        .order("created_at", { ascending: false });

      if (fonte === "members_directory" && (error || !data || data.length === 0)) {
        ({ data, error } = await supabase
          .from("members_public")
          .select("*")
          .eq("status", "active")
          .order("created_at", { ascending: false }));
      }

      if (!error && data && data.length > 0) {
        const dbMembers: Member[] = data.map((m) => ({
          id: String(m.id),
          name: m.full_name,
          profession: m.profession || "",
          description: m.description || "",
          photo: m.photo || "",
          photo_position: m.photo_position || "top",
          whatsapp: normalizeWhatsapp(m.whatsapp, m.country_code),
          email: m.email || "",
          instagram: m.instagram || "",
          city_state: m.city_state || "",
          website: m.website || "",
          services: m.services || "",
        }));
        // Quem tem foto aparece primeiro; dentro disso, quem escreveu a descrição (mesma ordem do carrossel da página inicial)
        const peso = (m: Member) => (m.photo ? 2 : 0) + (m.description ? 1 : 0);
        dbMembers.sort((a, b) => peso(b) - peso(a));
        setMembers(dbMembers);
      } else {
        const normalizedMocks = mockMembers.map((m) => ({
          ...m,
          whatsapp: normalizeWhatsapp(m.whatsapp),
        }));
        setMembers(normalizedMocks);
      }
      setLoading(false);
    }
    load();
  }, []);

  const professions = Array.from(new Set(members.map((m) => m.profession)));

  const filtered = members.filter((m) => {
    const matchesSearch =
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      (m.city_state || "").toLowerCase().includes(search.toLowerCase()) ||
      m.profession.toLowerCase().includes(search.toLowerCase()) ||
      (m.services || "").toLowerCase().includes(search.toLowerCase());
    const matchesProf = !professionFilter || m.profession === professionFilter;
    return matchesSearch && matchesProf;
  });

  return (
    <div className="min-h-screen bg-beige">
      <CommunityNavbar />
      <div className="pt-24 pb-16 md:pt-32 md:pb-24">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10 md:mb-14">
            {/* Colonial ornamental divider */}
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="inline-block w-8 h-px bg-primary/40" />
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" />
              <span className="inline-block w-16 h-px bg-primary/40" />
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" />
              <span className="inline-block w-8 h-px bg-primary/40" />
            </div>
            <p className="font-body text-xs tracking-[0.25em] uppercase text-primary mb-3">
              Nossa Comunidade
            </p>
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-brown mb-4">
              Mulheres que inspiram e conectam
            </h2>
            <p className="font-body text-brown/70 text-base leading-relaxed max-w-xl mx-auto">
              Conheça todas as integrantes da nossa comunidade. Use a busca ou
              filtros para encontrar alguém especial.
            </p>
          </div>

          {/* Search & Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-10 md:mb-14 max-w-3xl mx-auto">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-brown/30" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nome, cidade, profissão ou serviços..."
                className="w-full pl-11 pr-4 py-3 rounded-full border border-nude bg-white font-body text-sm text-brown placeholder-brown/30 focus:outline-none focus:border-primary transition-colors"
              />
            </div>
            <select
              value={professionFilter}
              onChange={(e) => setProfessionFilter(e.target.value)}
              className="px-4 py-3 rounded-full border border-nude bg-white font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors cursor-pointer"
            >
              <option value="">Todas as profissões</option>
              {professions.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          {/* Stats */}
          <div className="flex items-center justify-center gap-8 mb-10">
            <div className="text-center">
              <p className="font-display text-2xl text-primary">{members.length}</p>
              <p className="font-body text-xs text-brown/50 uppercase tracking-wide">
                Integrantes
              </p>
            </div>
            <div className="w-px h-8 bg-nude" />
            <div className="text-center">
              <p className="font-display text-2xl text-primary">
                {professions.length}
              </p>
              <p className="font-body text-xs text-brown/50 uppercase tracking-wide">
                Áreas
              </p>
            </div>
          </div>

          {/* Member Grid */}
          {loading ? (
            <div className="text-center py-20">
              <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
              <p className="font-body text-sm text-brown/50">Carregando comunidade...</p>
            </div>
          ) : (
            <MemberGrid members={filtered} />
          )}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-nude mx-auto mb-4">
                <i className="ri-search-line text-2xl text-brown/30" />
              </div>
              <p className="font-body text-brown/50 text-base">
                Nenhuma integrante encontrada com esses filtros.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-brown py-8">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-7xl mx-auto text-center">
          <p className="font-body text-sm text-white/60">
            &copy; {new Date().getFullYear()} Entre Elas. Todos os direitos reservados.
          </p>
          <p className="font-body text-xs text-white/40 mt-1">
            Uma comunidade de mulheres que se apoiam e crescem juntas.
          </p>
        </div>
      </footer>
    </div>
  );
}